"use client";

import React from "react";
import { Activity, ShieldCheck, Compass, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LongevityPhilosophy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  const principles = [{
    label: "Measure",
    desc: "Objective baseline data"
  }, {
    label: "Understand",
    desc: "Clinical interpretation"
  }, {
    label: "Improve",
    desc: "Targeted interventions"
  }, {
    label: "Reassess",
    desc: "Track longitudinal change"
  }];
  return <section id="longevityphilosophy" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 lg:py-28 border-b border-border overflow-hidden isolate">
      {/* Subtle clinical background hairline decoration */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-30 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" aria-hidden="true">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div style={{
        opacity: isVisible ? 1 : 0
      }} className={`transition-all duration-700 ease-out transform ${isVisible ? "translate-y-0" : "translate-y-6"}`}>
          {/* Section Core Header */}
          <div className="max-w-3xl mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
              What do we mean by longevity?
            </h2>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column: Primary Content & Discipline Framing */}
            <div className="lg:col-span-7 space-y-6">
              <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border shadow-xs">
                <p className="text-xl sm:text-2xl font-medium text-foreground leading-relaxed">
                  Longevity is not simply about living for more years. It is also about maintaining the physical capacity to remain active, independent and able to do the things that matter to you as you age.
                </p>
              </div>

              <div className="p-6 sm:p-8 rounded-2xl bg-secondary/35 border border-border/80 space-y-4">
                <p className="text-base sm:text-lg text-foreground/90 leading-relaxed">
                  PhysAnalytica does not predict an individual's lifespan. Instead, we objectively measure aspects of fitness, movement and metabolism that can help build a clearer picture of current physical capacity.
                </p>
                <Separator className="bg-border/70 my-3" />
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Physiotherapy is PhysAnalytica's core discipline, and biomechanics, physiology and fitness testing exist to serve that same goal of moving better with less pain, now extended across the whole of adult life.
                </p>
              </div>
            </div>

            {/* Right Column: Highlighted Verbatim Statement */}
            <div className="lg:col-span-5">
              <Card className="border border-primary/20 bg-card shadow-sm rounded-2xl overflow-hidden relative">
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-chart-2 to-primary" />
                
                <CardContent className="p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-primary font-medium text-xs tracking-wider uppercase">
                      <Activity className="size-4 text-primary" />
                      <span>The Clinical Cycle</span>
                    </div>

                    {/* Verbatim Highlighted Statement */}
                    <div className="space-y-3 pt-2">
                      <p className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
                        Measure. Understand. Improve. Reassess.
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        A continuous, objective framework designed to support functional vitality, musculoskeletal resilience, and physical independence at every stage of life.
                      </p>
                    </div>

                    {/* Step-by-step cycle visualization */}
                    <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
                      {principles.map((step, idx) => <div key={step.label} data-index={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/60 transition-colors hover:bg-muted/70">
                          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-mono text-xs font-semibold">
                            0{idx + 1}
                          </span>
                          <div>
                            <span className="font-semibold text-sm text-foreground block">
                              {step.label}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {step.desc}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}